import { DOCUMENT, CommonModule } from '@angular/common';
import { isDevMode, SecurityContext, Injectable, RendererFactory2, Optional, Inject, Directive, ElementRef, Renderer2, Input, NgModule } from '@angular/core';
import { generate } from '@ant-design/colors';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject, of, Observable } from 'rxjs';
import { map, tap, finalize, catchError, share, filter, take } from 'rxjs/operators';

import * as ɵngcc0 from '@angular/core';
import * as ɵngcc1 from '@angular/common/http';
import * as ɵngcc2 from '@angular/platform-browser';
const ANT_ICON_ANGULAR_CONSOLE_PREFIX = '[@ant-design/icons-angular]:';
function error(message) {
    console.error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX} ${message}.`);
}
function warn(message) {
    if (isDevMode()) {
        console.warn(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX} ${message}.`);
    }
}
function getSecondaryColor(primaryColor) {
    return generate(primaryColor)[0];
}
function withSuffix(name, theme) {
    switch (theme) {
        case 'fill': return `${name}-fill`;
        case 'outline': return `${name}-o`;
        case 'twotone': return `${name}-twotone`;
        case undefined: return name;
        default: throw new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}Theme "${theme}" is not a recognized theme!`);
    }
}
function withSuffixAndColor(name, theme, pri, sec) {
    return `${withSuffix(name, theme)}-${pri}-${sec}`;
}
function mapAbbrToTheme(abbr) {
    return abbr === 'o' ? 'outline' : abbr;
}
function alreadyHasAThemeSuffix(name) {
    return name.endsWith('-fill') || name.endsWith('-o') || name.endsWith('-twotone');
}
function isIconDefinition(target) {
    return (typeof target === 'object' &&
        typeof target.name === 'string' &&
        (typeof target.theme === 'string' || target.theme === undefined) &&
        typeof target.icon === 'string');
}
/**
 * Get an `IconDefinition` object from abbreviation type, like `account-book-fill`.
 * @param str
 */
function getIconDefinitionFromAbbr(str) {
    const arr = str.split('-');
    const theme = mapAbbrToTheme(arr.splice(arr.length - 1, 1)[0]);
    const name = arr.join('-');
    return {
        name,
        theme,
        icon: ''
    };
}
function cloneSVG(svg) {
    return svg.cloneNode(true);
}
/**
 * Parse inline SVG string and replace colors with placeholders. For twotone icons only.
 */
function replaceFillColor(raw) {
    return raw
        .replace(/['"]#333['"]/g, '"primaryColor"')
        .replace(/['"]#E6E6E6['"]/g, '"secondaryColor"')
        .replace(/['"]#D9D9D9['"]/g, '"secondaryColor"')
        .replace(/['"]#D8D8D8['"]/g, '"secondaryColor"');
}
/**
 * Split a name with namespace in it into a tuple like [ name, namespace ].
 */
function getNameAndNamespace(type) {
    const split = type.split(':');
    switch (split.length) {
        case 1: return [type, ''];
        case 2: return [split[1], split[0]];
        default: throw new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}The icon type ${type} is not valid!`);
    }
}
function hasNamespace(type) {
    return getNameAndNamespace(type)[1] !== '';
}

function NameSpaceIsNotSpecifyError() {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}Type should have a namespace. Try "namespace:${name}".`);
}
function IconNotFoundError(icon) {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}the icon ${icon} does not exist or is not registered.`);
}
function HttpModuleNotImport() {
    error(`you need to import "HttpClientModule" to use dynamic importing.`);
    return null;
}
function UrlNotSafeError(url) {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}The url "${url}" is unsafe.`);
}
function SVGTagNotFoundError() {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}<svg> tag not found.`);
}
function DynamicLoadingTimeoutError() {
    return new Error(`${ANT_ICON_ANGULAR_CONSOLE_PREFIX}Importing timeout error.`);
}

const JSONP_HANDLER_NAME = '__ant_icon_load';
class IconService {
    constructor(_rendererFactory, _handler, 
    // tslint:disable-next-line:no-any
    _document, sanitizer) {
        this._rendererFactory = _rendererFactory;
        this._handler = _handler;
        this._document = _document;
        this.sanitizer = sanitizer;
        this.defaultTheme = 'outline';
        /**
         * All icon definitions would be registered here.
         */
        this._svgDefinitions = new Map();
        /**
         * Cache all rendered icons. Icons are identified by name, theme,
         * and for twotone icons, primary color and secondary color.
         */
        this._svgRenderedDefinitions = new Map();
        this._inProgressFetches = new Map();
        /**
         * Url prefix for fetching inline SVG by dynamic importing.
         */
        this._assetsUrlRoot = '';
        this._twoToneColorPalette = {
            primaryColor: '#333333',
            secondaryColor: '#E6E6E6'
        };
        /** A flag indicates whether jsonp loading is enabled. */
        this._enableJsonpLoading = false;
        this._jsonpIconLoad$ = new Subject();
        this._renderer = this._rendererFactory.createRenderer(null, null);
        if (this._handler) {
            this._http = new HttpClient(this._handler);
        }
    }
    set twoToneColor({ primaryColor, secondaryColor }) {
        this._twoToneColorPalette.primaryColor = primaryColor;
        this._twoToneColorPalette.secondaryColor =
            secondaryColor || getSecondaryColor(primaryColor);
    }
    get twoToneColor() {
        // Make a copy to avoid unexpected changes.
        return Object.assign({}, this._twoToneColorPalette);
    }
    /**
     * Call this method to switch to jsonp like loading.
     */
    useJsonpLoading() {
        if (!this._enableJsonpLoading) {
            this._enableJsonpLoading = true;
            window[JSONP_HANDLER_NAME] = (icon) => {
                this._jsonpIconLoad$.next(icon);
            };
        }
        else {
            warn('You are already using jsonp loading.');
        }
    }
    /**
     * Change the prefix of the inline svg resources, so they could be deployed elsewhere, like CDN.
     * @param prefix
     */
    changeAssetsSource(prefix) {
        this._assetsUrlRoot = prefix.endsWith('/') ? prefix : prefix + '/';
    }
    /**
     * Add icons provided by ant design.
     * @param icons
     */
    addIcon(...icons) {
        icons.forEach(icon => {
            this._svgDefinitions.set(withSuffix(icon.name, icon.theme), icon);
        });
    }
    /**
     * Register an icon. Namespace is required.
     * @param type
     * @param literal
     */
    addIconLiteral(type, literal) {
        const [_, namespace] = getNameAndNamespace(type);
        if (!namespace) {
            throw NameSpaceIsNotSpecifyError();
        }
        this.addIcon({ name: type, icon: literal });
    }
    /**
     * Remove all cache.
     */
    clear() {
        this._svgDefinitions.clear();
        this._svgRenderedDefinitions.clear();
    }
    /**
     * Get a rendered `SVGElement`.
     * @param icon
     * @param twoToneColor
     */
    getRenderedContent(icon, twoToneColor) {
        // If `icon` is a `IconDefinition`, go to the next step. If not, try to fetch it from cache.
        const definitionOrNull = isIconDefinition(icon)
            ? icon
            : this._svgDefinitions.get(icon) || null;
        // If `icon` is a `IconDefinition` of successfully fetch, wrap it in an `Observable`.
        // Otherwise try to fetch it from remote.
        const $iconDefinition = definitionOrNull
            ? of(definitionOrNull)
            : this._loadIconDynamically(icon);
        // If finally get an `IconDefinition`, render and return it. Otherwise throw an error.
        return $iconDefinition.pipe(map(i => {
            if (!i) {
                throw IconNotFoundError(icon);
            }
            return this._loadSVGFromCacheOrCreateNew(i, twoToneColor);
        }));
    }
    getCachedIcons() {
        return this._svgDefinitions;
    }
    /**
     * Get raw svg and assemble a `IconDefinition` object.
     * @param type
     */
    _loadIconDynamically(type) {
        // If developer doesn't provide HTTP module nor enable jsonp loading, just throw an error.
        if (!this._http && !this._enableJsonpLoading) {
            return of(HttpModuleNotImport());
        }
        // If multi directive ask for the same icon at the same time,
        // request should only be fired once.
        let inProgress = this._inProgressFetches.get(type);
        if (!inProgress) {
            const [name, namespace] = getNameAndNamespace(type);
            // If the string has a namespace within, create a simple `IconDefinition`.
            const icon = namespace
                ? { name: type, icon: '' }
                : getIconDefinitionFromAbbr(name);
            const suffix = this._enableJsonpLoading ? '.js' : '.svg';
            const url = (namespace
                ? `${this._assetsUrlRoot}assets/${namespace}/${name}`
                : `${this._assetsUrlRoot}assets/${icon.theme}/${icon.name}`) + suffix;
            const safeUrl = this.sanitizer.sanitize(SecurityContext.URL, url);
            if (!safeUrl) {
                throw UrlNotSafeError(url);
            }
            const source = !this._enableJsonpLoading
                ? this._http
                    .get(safeUrl, { responseType: 'text' })
                    .pipe(map(literal => (Object.assign(Object.assign({}, icon), { icon: literal }))))
                : this._loadIconDynamicallyWithJsonp(icon, safeUrl);
            inProgress = source.pipe(tap(definition => this.addIcon(definition)), finalize(() => this._inProgressFetches.delete(type)), catchError(() => of(null)), share());
            this._inProgressFetches.set(type, inProgress);
        }
        return inProgress;
    }
    _loadIconDynamicallyWithJsonp(icon, url) {
        return new Observable(subscriber => {
            const loader = this._document.createElement('script');
            const timer = setTimeout(() => {
                clean();
                subscriber.error(DynamicLoadingTimeoutError());
            }, 6000);
            loader.src = url;
            function clean() {
                loader.parentNode.removeChild(loader);
                clearTimeout(timer);
            }
            this._document.body.appendChild(loader);
            this._jsonpIconLoad$
                .pipe(filter(i => i.name === icon.name && i.theme === icon.theme), take(1))
                .subscribe(i => {
                subscriber.next(i);
                clean();
            });
        });
    }
    /**
     * Render a new `SVGElement` for a given `IconDefinition`, or make a copy from cache.
     * @param icon
     * @param twoToneColor
     */
    _loadSVGFromCacheOrCreateNew(icon, twoToneColor) {
        let svg;
        const pri = twoToneColor || this._twoToneColorPalette.primaryColor;
        const sec = getSecondaryColor(pri) || this._twoToneColorPalette.secondaryColor;
        const key = icon.theme === 'twotone'
            ? withSuffixAndColor(icon.name, icon.theme, pri, sec)
            : icon.theme === undefined
                ? icon.name
                : withSuffix(icon.name, icon.theme);
        // Try to make a copy from cache.
        const cached = this._svgRenderedDefinitions.get(key);
        if (cached) {
            svg = cached.icon;
        }
        else {
            svg = this._setSVGAttribute(this._colorizeSVGIcon(
            // Icons provided by ant design should be refined to remove preset colors.
            this._createSVGElementFromString(hasNamespace(icon.name) ? icon.icon : replaceFillColor(icon.icon)), icon.theme === 'twotone', pri, sec));
            // Cache it.
            this._svgRenderedDefinitions.set(key, Object.assign(Object.assign({}, icon), { icon: svg }));
        }
        return cloneSVG(svg);
    }
    _createSVGElementFromString(str) {
        const div = this._document.createElement('div');
        div.innerHTML = str;
        const svg = div.querySelector('svg');
        if (!svg) {
            throw SVGTagNotFoundError;
        }
        return svg;
    }
    _setSVGAttribute(svg) {
        this._renderer.setAttribute(svg, 'width', '1em');
        this._renderer.setAttribute(svg, 'height', '1em');
        return svg;
    }
    _colorizeSVGIcon(svg, twotone, pri, sec) {
        if (twotone) {
            const children = svg.childNodes;
            const length = children.length;
            for (let i = 0; i < length; i++) {
                const child = children[i];
                if (child.getAttribute('fill') === 'secondaryColor') {
                    this._renderer.setAttribute(child, 'fill', sec);
                }
                else {
                    this._renderer.setAttribute(child, 'fill', pri);
                }
            }
        }
        this._renderer.setAttribute(svg, 'fill', 'currentColor');
        return svg;
    }
}
IconService.ɵfac = function IconService_Factory(t) { return new (t || IconService)(ɵngcc0.ɵɵinject(ɵngcc0.RendererFactory2), ɵngcc0.ɵɵinject(ɵngcc1.HttpBackend, 8), ɵngcc0.ɵɵinject(DOCUMENT, 8), ɵngcc0.ɵɵinject(ɵngcc2.DomSanitizer)); };
IconService.ɵprov = ɵngcc0.ɵɵdefineInjectable({ token: IconService, factory: IconService.ɵfac });
IconService.ctorParameters = () => [
    { type: RendererFactory2 },
    { type: HttpBackend, decorators: [{ type: Optional }] },
    { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [DOCUMENT,] }] },
    { type: DomSanitizer }
];
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IconService, [{
        type: Injectable
    }], function () { return [{ type: ɵngcc0.RendererFactory2 }, { type: ɵngcc1.HttpBackend, decorators: [{
                type: Optional
            }] }, { type: undefined, decorators: [{
                type: Optional
            }, {
                type: Inject,
                args: [DOCUMENT]
            }] }, { type: ɵngcc2.DomSanitizer }]; }, null); })();

function checkMeta(prev, after) {
    return prev.type === after.type && prev.theme === after.theme && prev.twoToneColor === after.twoToneColor;
}
class IconDirective {
    constructor(_iconService, _elementRef, _renderer) {
        this._iconService = _iconService;
        this._elementRef = _elementRef;
        this._renderer = _renderer;
    }
    ngOnChanges(changes) {
        if (changes.type || changes.theme || changes.twoToneColor) {
            this._changeIcon();
        }
    }
    /**
     * Render a new icon in the current element. Remove the icon when `type` is falsy.
     */
    _changeIcon() {
        return new Promise(resolve => {
            if (!this.type) {
                this._clearSVGElement();
                resolve(null);
            }
            else {
                const preMeta = this._getSelfRenderMeta();
                this._iconService.getRenderedContent(this._parseIconType(this.type, this.theme), this.twoToneColor).subscribe(svg => {
                    // avoid race condition, see https://github.com/ant-design/ant-design-icons/issues/315
                    if (checkMeta(preMeta, this._getSelfRenderMeta())) {
                        this._setSVGElement(svg);
                        resolve(svg);
                    }
                    else {
                        resolve(null);
                    }
                });
            }
        });
    }
    _getSelfRenderMeta() {
        return {
            type: this.type,
            theme: this.theme,
            twoToneColor: this.twoToneColor
        };
    }
    /**
     * Parse a icon to the standard form, an `IconDefinition` or a string like 'account-book-fill` (with a theme suffixed).
     * If namespace is specified, ignore theme because it meaningless for users' icons.
     *
     * @param type
     * @param theme
     */
    _parseIconType(type, theme) {
        if (isIconDefinition(type)) {
            return type;
        }
        else {
            const [name, namespace] = getNameAndNamespace(type);
            if (namespace) {
                return type;
            }
            if (alreadyHasAThemeSuffix(name)) {
                if (!!theme) {
                    warn(`'type' ${name} already gets a theme inside so 'theme' ${theme} would be ignored`);
                }
                return name;
            }
            else {
                return withSuffix(name, theme || this._iconService.defaultTheme);
            }
        }
    }
    _setSVGElement(svg) {
        this._clearSVGElement();
        this._renderer.appendChild(this._elementRef.nativeElement, svg);
    }
    _clearSVGElement() {
        var _a;
        const el = this._elementRef.nativeElement;
        const children = el.childNodes;
        const length = children.length;
        for (let i = length - 1; i >= 0; i--) {
            const child = children[i];
            if (((_a = child.tagName) === null || _a === void 0 ? void 0 : _a.toLowerCase()) === 'svg') {
                this._renderer.removeChild(el, child);
            }
        }
    }
}
IconDirective.ɵfac = function IconDirective_Factory(t) { return new (t || IconDirective)(ɵngcc0.ɵɵdirectiveInject(IconService), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef), ɵngcc0.ɵɵdirectiveInject(ɵngcc0.Renderer2)); };
IconDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: IconDirective, selectors: [["", "antIcon", ""]], inputs: { type: "type", theme: "theme", twoToneColor: "twoToneColor" }, features: [ɵngcc0.ɵɵNgOnChangesFeature] });
IconDirective.ctorParameters = () => [
    { type: IconService },
    { type: ElementRef },
    { type: Renderer2 }
];
IconDirective.propDecorators = {
    type: [{ type: Input }],
    theme: [{ type: Input }],
    twoToneColor: [{ type: Input }]
};
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IconDirective, [{
        type: Directive,
        args: [{
                selector: '[antIcon]'
            }]
    }], function () { return [{ type: IconService }, { type: ɵngcc0.ElementRef }, { type: ɵngcc0.Renderer2 }]; }, { type: [{
            type: Input
        }], theme: [{
            type: Input
        }], twoToneColor: [{
            type: Input
        }] }); })();

class IconModule {
}
IconModule.ɵfac = function IconModule_Factory(t) { return new (t || IconModule)(); };
IconModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: IconModule });
IconModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ providers: [IconService], imports: [[CommonModule]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(IconModule, { declarations: function () { return [IconDirective]; }, imports: function () { return [CommonModule]; }, exports: function () { return [IconDirective]; } }); })();
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && ɵngcc0.ɵsetClassMetadata(IconModule, [{
        type: NgModule,
        args: [{
                imports: [CommonModule],
                exports: [IconDirective],
                declarations: [IconDirective],
                providers: [IconService]
            }]
    }], null, null); })();

const manifest = {
    fill: [
        'account-book', 'aliwangwang', 'alipay-circle', 'android', 'alipay-square', 'amazon-square', 'amazon-circle', 'api', 'appstore', 'backward', 'bank', 'audio', 'behance-square', 'behance-circle', 'book', 'bell', 'box-plot', 'alert', 'bulb', 'calculator', 'camera', 'apple', 'build', 'bug', 'caret-up', 'caret-right', 'car', 'caret-left', 'check-circle', 'check-square', 'ci-circle', 'calendar', 'caret-down', 'carry-out', 'close-square', 'cloud', 'close-circle', 'code-sandbox-square', 'code-sandbox-circle', 'codepen-square', 'code', 'contacts', 'codepen-circle', 'container', 'clock-circle', 'control', 'chrome', 'copy', 'copyright-circle', 'crown', 'customer-service', 'compass', 'dashboard', 'database', 'delete', 'dingtalk-circle', 'dollar-circle', 'dingtalk-square', 'diff', 'down-square', 'dropbox-square', 'dropbox-circle', 'dribbble-square', 'dribbble-circle', 'edit', 'down-circle', 'euro-circle', 'credit-card', 'exclamation-circle', 'dislike', 'environment', 'eye-invisible', 'fast-backward', 'facebook', 'experiment', 'fast-forward', 'file-excel', 'file-add', 'file-exclamation', 'file-pdf', 'file-markdown', 'file', 'eye', 'file-ppt', 'file-image', 'file-unknown', 'file-word', 'filter', 'flag', 'folder-add', 'folder-open', 'folder', 'fire', 'frown', 'fund', 'funnel-plot', 'format-painter', 'forward', 'gift', 'gitlab', 'file-text', 'github', 'gold', 'google-plus-circle', 'google-plus-square', 'heart', 'hdd', 'google-circle', 'golden', 'highlight', 'hourglass', 'google-square', 'idcard', 'home', 'html5', 'ie-circle', 'info-circle', 'ie-square', 'insurance', 'interaction', 'instagram', 'layout', 'left-circle', 'left-square', 'like', 'linkedin', 'mail', 'medicine-box', 'lock', 'meh', 'mac-command', 'medium-circle', 'medium-square', 'message', 'minus-square', 'money-collect', 'minus-circle', 'notification', 'pay-circle', 'pause-circle', 'picture', 'phone', 'mobile', 'play-circle', 'plus-square', 'file-zip', 'plus-circle', 'pound-circle', 'play-square', 'profile', 'printer', 'pushpin', 'property-safety', 'pie-chart', 'qq-circle', 'question-circle', 'qq-square', 'read', 'red-envelope', 'reddit-square', 'reddit-circle', 'rest', 'right-square', 'right-circle', 'rocket', 'robot', 'project', 'reconciliation', 'schedule', 'save', 'security-scan', 'shop', 'shopping', 'sketch-circle', 'setting', 'skin', 'signal', 'safety-certificate', 'slack-circle', 'sketch-square', 'sliders', 'smile', 'skype', 'snippets', 'sound', 'star', 'step-backward', 'stop', 'step-forward', 'tablet', 'slack-square', 'tags', 'taobao-circle', 'taobao-square', 'thunderbolt', 'tool', 'trophy', 'trademark-circle', 'switcher', 'unlock', 'twitter-square', 'up-square', 'up-circle', 'twitter-circle', 'usb', 'wallet', 'video-camera', 'wechat', 'warning', 'weibo-circle', 'weibo-square', 'windows', 'youtube', 'yahoo', 'yuque', 'zhihu-circle', 'tag', 'zhihu-square'
    ],
    outline: [
        'account-book', 'alibaba', 'align-center', 'align-right', 'align-left', 'amazon', 'alipay-circle', 'android', 'ant-design', 'aliwangwang', 'apartment', 'appstore', 'appstore-add', 'alipay', 'api', 'ant-cloud', 'arrow-left', 'arrows-alt', 'area-chart', 'arrow-right', 'apple', 'arrow-up', 'arrow-down', 'aim', 'barcode', 'audio-muted', 'bank', 'bar-chart', 'behance', 'audit', 'block', 'bars', 'book', 'behance-square', 'border-inner', 'border-bottom', 'border-horizontal', 'border-left', 'border-outer', 'backward', 'alert', 'border-right', 'bell', 'bold', 'border-verticle', 'border', 'borderless-table', 'bg-colors', 'box-plot', 'border-top', 'audio', 'bug', 'bulb', 'calendar', 'build', 'aliyun', 'branches', 'caret-left', 'car', 'calculator', 'camera', 'check-circle', 'check-square', 'check', 'carry-out', 'caret-down', 'caret-up', 'caret-right', 'clear', 'ci-circle', 'close-square', 'close', 'cloud-download', 'cloud', 'cloud-upload', 'cloud-server', 'clock-circle', 'code', 'ci', 'coffee', 'cloud-sync', 'cluster', 'column-height', 'code-sandbox', 'comment', 'console-sql', 'compass', 'column-width', 'chrome', 'compress', 'codepen', 'codepen-circle', 'close-circle', 'container', 'copy', 'credit-card', 'crown', 'dash', 'dashboard', 'database', 'copyright-circle', 'delete-column', 'delete-row', 'deployment-unit', 'desktop', 'contacts', 'dingtalk', 'dingding', 'delivered-procedure', 'diff', 'delete', 'disconnect', 'dislike', 'dollar-circle', 'customer-service', 'double-right', 'down-circle', 'dot-chart', 'download', 'drag', 'copyright', 'down', 'dollar', 'down-square', 'control', 'edit', 'double-left', 'ellipsis', 'enter', 'dropbox', 'dribbble-square', 'environment', 'exception', 'exclamation', 'expand-alt', 'euro', 'eye', 'dribbble', 'experiment', 'exclamation-circle', 'facebook', 'fall', 'field-number', 'expand', 'fast-backward', 'field-string', 'field-time', 'file-exclamation', 'euro-circle', 'field-binary', 'file-done', 'file-excel', 'file-gif', 'file-markdown', 'file-add', 'file-pdf', 'file-jpg', 'file-image', 'file', 'file-protect', 'file-sync', 'file-unknown', 'eye-invisible', 'file-text', 'export', 'file-search', 'file-word', 'file-zip', 'filter', 'flag', 'folder-add', 'fire', 'folder-view', 'folder', 'folder-open', 'fork', 'form', 'format-painter', 'font-size', 'font-colors', 'function', 'fund', 'fullscreen', 'fund-projection-screen', 'frown', 'fullscreen-exit', 'forward', 'fund-view', 'gift', 'fast-forward', 'gateway', 'gold', 'global', 'google-plus', 'google', 'gitlab', 'gif', 'funnel-plot', 'group', 'hdd', 'github', 'heart', 'highlight', 'hourglass', 'history', 'home', 'html5', 'idcard', 'heat-map', 'import', 'info-circle', 'insert-row-above', 'insert-row-right', 'instagram', 'insurance', 'ie', 'inbox', 'insert-row-below', 'insert-row-left', 'laptop', 'interaction', 'layout', 'key', 'italic', 'issues-close', 'info', 'left-circle', 'left-square', 'line-height', 'like', 'left', 'line-chart', 'line', 'linkedin', 'link', 'loading', 'login', 'loading-3-quarters', 'lock', 'logout', 'medium', 'man', 'mail', 'mac-command', 'medicine-box', 'file-ppt', 'merge-cells', 'menu', 'message', 'minus-circle', 'minus', 'mobile', 'minus-square', 'menu-fold', 'money-collect', 'monitor', 'node-collapse', 'more', 'node-expand', 'medium-workmark', 'notification', 'partition', 'paper-clip', 'ordered-list', 'menu-unfold', 'node-index', 'pause-circle', 'percentage', 'pause', 'number', 'pay-circle', 'phone', 'pic-center', 'one-to-one', 'picture', 'pie-chart', 'play-circle', 'pic-left', 'play-square', 'plus-circle', 'plus-square', 'plus', 'pound-circle', 'meh', 'poweroff', 'printer', 'pound', 'pic-right', 'profile', 'qq', 'property-safety', 'pull-request', 'qrcode', 'radius-bottomright', 'radar-chart', 'radius-upleft', 'question-circle', 'read', 'radius-setting', 'radius-upright', 'reconciliation', 'red-envelope', 'retweet', 'right-circle', 'reload', 'reddit', 'rest', 'rise', 'right', 'robot', 'right-square', 'rotate-right', 'rollback', 'safety-certificate', 'redo', 'rocket', 'rotate-left', 'pushpin', 'question', 'save', 'safety', 'schedule', 'scissor', 'search', 'security-scan', 'select', 'scan', 'shake', 'send', 'share-alt', 'shopping', 'shrink', 'shopping-cart', 'radius-bottomleft', 'project', 'setting', 'skype', 'sliders', 'slack-square', 'skin', 'shop', 'small-dash', 'sort-ascending', 'sound', 'snippets', 'solution', 'sort-descending', 'star', 'split-cells', 'smile', 'strikethrough', 'subnode', 'swap-left', 'swap', 'stock', 'sketch', 'stop', 'sisternode', 'slack', 'step-forward', 'step-backward', 'table', 'switcher', 'sync', 'tags', 'taobao-circle', 'thunderbolt', 'taobao', 'team', 'to-top', 'transaction', 'trademark-circle', 'trademark', 'twitter', 'underline', 'undo', 'ungroup', 'trophy', 'tool', 'translation', 'unordered-list', 'unlock', 'up-square', 'user-add', 'user-delete', 'user', 'usb', 'user-switch', 'usergroup-add', 'usergroup-delete', 'tablet', 'video-camera-add', 'upload', 'vertical-align-middle', 'vertical-right', 'up', 'vertical-align-top', 'wechat', 'video-camera', 'wallet', 'warning', 'weibo-circle', 'whats-app', 'weibo-square', 'vertical-left', 'wifi', 'weibo', 'windows', 'vertical-align-bottom', 'yahoo', 'woman', 'yuque', 'zhihu', 'up-circle', 'zoom-in', 'youtube', 'verified', 'swap-right', 'tag', 'zoom-out'
    ],
    twotone: [
        'account-book', 'api', 'appstore', 'audio', 'bank', 'bell', 'alert', 'build', 'bug', 'camera', 'calendar', 'book', 'bulb', 'calculator', 'box-plot', 'carry-out', 'check-circle', 'check-square', 'ci-circle', 'clock-circle', 'close-circle', 'close-square', 'cloud', 'ci', 'car', 'code', 'compass', 'copyright-circle', 'control', 'copyright', 'credit-card', 'container', 'customer-service', 'dashboard', 'contacts', 'copy', 'delete', 'database', 'diff', 'crown', 'dollar-circle', 'dislike', 'down-circle', 'dollar', 'down-square', 'edit', 'exclamation-circle', 'euro', 'eye-invisible', 'environment', 'eye', 'experiment', 'file-add', 'file-exclamation', 'euro-circle', 'file-markdown', 'file-image', 'file-excel', 'file-ppt', 'file', 'file-zip', 'file-word', 'file-text', 'fire', 'folder-add', 'file-pdf', 'flag', 'filter', 'folder', 'folder-open', 'frown', 'gift', 'funnel-plot', 'fund', 'home', 'highlight', 'hdd', 'hourglass', 'gold', 'info-circle', 'html5', 'heart', 'idcard', 'insurance', 'interaction', 'layout', 'left-circle', 'like', 'left-square', 'mail', 'file-unknown', 'lock', 'meh', 'message', 'medicine-box', 'minus-circle', 'mobile', 'money-collect', 'notification', 'minus-square', 'pause-circle', 'pie-chart', 'plus-circle', 'pound-circle', 'printer', 'plus-square', 'play-square', 'play-circle', 'profile', 'pushpin', 'picture', 'phone', 'property-safety', 'question-circle', 'reconciliation', 'red-envelope', 'right-circle', 'right-square', 'rest', 'safety-certificate', 'rocket', 'schedule', 'security-scan', 'setting', 'save', 'shop', 'project', 'shopping', 'skin', 'smile', 'snippets', 'sound', 'star', 'sliders', 'stop', 'switcher', 'tablet', 'tags', 'thunderbolt', 'tool', 'trademark-circle', 'trophy', 'unlock', 'tag', 'usb', 'up-circle', 'up-square', 'wallet', 'video-camera', 'warning'
    ]
};

/**
 * Generated bundle index. Do not edit.
 */

export { ANT_ICON_ANGULAR_CONSOLE_PREFIX, DynamicLoadingTimeoutError, HttpModuleNotImport, IconDirective, IconModule, IconNotFoundError, IconService, NameSpaceIsNotSpecifyError, SVGTagNotFoundError, UrlNotSafeError, alreadyHasAThemeSuffix, cloneSVG, error, getIconDefinitionFromAbbr, getNameAndNamespace, getSecondaryColor, hasNamespace, isIconDefinition, manifest, mapAbbrToTheme, replaceFillColor, warn, withSuffix, withSuffixAndColor };

//# sourceMappingURL=ant-design-icons-angular.js.map